import { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';

interface Chapter {
  id: string;
  name: string;
  city: string;
  state: string;
  country: string;
  contactEmail: string | null;
  presidentName: string;
  secretaryName: string;
  inceptionYear: number | null;
  reunionHostYears: string | null;
  chapterFact: string | null;
}

export function MobileChaptersView() {
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const [guestCount, setGuestCount] = useState<number | null>(null);
  const [tableDistribution, setTableDistribution] = useState<{ name: string; count: number; zone: string }[]>([]);
  const [executives, setExecutives] = useState<{ id: string; name: string; title: string; phone: string | null }[]>([]);
  const [heritageMembers, setHeritageMembers] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    async function fetch() {
      try {
        const data = await apiClient<Chapter[]>('/chapters');
        setChapters(data);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, []);

  // Fetch guest count and executives when a chapter detail is opened
  useEffect(() => {
    if (selectedChapter) {
      setGuestCount(null);
      setTableDistribution([]);
      apiClient<{ count: number }>(`/chapters/${selectedChapter.id}/guest-count`)
        .then(({ count }) => setGuestCount(count))
        .catch(() => setGuestCount(0));
      apiClient<{ guests: { id: string; fullName: string; ticketNumber: string | null; tableName: string | null; zoneName: string | null }[] }>(`/chapters/${selectedChapter.id}/guests`)
        .then(({ guests }) => {
          const countMap = new Map<string, { count: number; zone: string }>();
          let unseated = 0;
          for (const g of guests) {
            if (g.tableName) {
              const existing = countMap.get(g.tableName);
              if (existing) {
                existing.count++;
              } else {
                countMap.set(g.tableName, { count: 1, zone: g.zoneName || '—' });
              }
            } else {
              unseated++;
            }
          }
          const dist = Array.from(countMap.entries()).map(([name, v]) => ({ name, count: v.count, zone: v.zone })).sort((a, b) => a.name.localeCompare(b.name));
          if (unseated > 0) {
            dist.push({ name: 'Unseated', count: unseated, zone: '—' });
          }
          setTableDistribution(dist);
        })
        .catch(() => setTableDistribution([]));

      // Fetch executives only for Georgia chapter
      const isGeorgia = selectedChapter.name.toLowerCase().includes('georgia');
      if (isGeorgia) {
        apiClient<{ id: string; name: string; title: string; phone: string | null }[]>('/public/executives')
          .then((data) => setExecutives(data))
          .catch(() => setExecutives([]));
        apiClient<{ id: string; name: string }[]>('/public/heritage-members')
          .then((data) => setHeritageMembers(data))
          .catch(() => setHeritageMembers([]));
      } else {
        setExecutives([]);
        setHeritageMembers([]);
      }
    }
  }, [selectedChapter]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading chapters...</div>
      </div>
    );
  }

  // ── Detail page for a selected chapter ──────────────────────
  if (selectedChapter) {
    return (
      <div style={styles.container}>
        {/* Back arrow */}
        <button
          onClick={() => setSelectedChapter(null)}
          style={styles.backBtn}
          aria-label="Back to chapters"
        >
          ← Back
        </button>

        {/* Chapter name header */}
        <h1 style={styles.detailTitle}>{selectedChapter.name.toUpperCase()}</h1>

        {/* Detail tiles in a 2x2 grid */}
        <div style={styles.detailGrid}>
          <div style={styles.detailCard}>
            <div style={styles.detailLabel}>FOUNDED</div>
            <div style={styles.detailValue}>
              {selectedChapter.inceptionYear || 'N/A'}
            </div>
          </div>
          <div style={styles.detailCard}>
            <div style={styles.detailLabel}>COUNTRY</div>
            <div style={styles.detailValue}>{selectedChapter.country}</div>
          </div>
          <div style={styles.detailCard}>
            <div style={styles.detailLabel}>PRESIDENT</div>
            <div style={styles.detailValue}>
              {selectedChapter.presidentName || 'N/A'}
            </div>
          </div>
          <div style={styles.detailCard}>
            <div style={styles.detailLabel}>CONTACT</div>
            <div style={styles.detailValue}>
              {selectedChapter.contactEmail || 'N/A'}
            </div>
          </div>
          {/* Guest Count tile */}
          <div style={{ ...styles.detailCard, aspectRatio: 'unset', minHeight: '80px', gridColumn: '1 / -1' }}>
            <div style={styles.detailLabel}>GUESTS REGISTERED FOR 2026 REUNION</div>
            <div style={styles.detailValue}>{guestCount ?? '...'}</div>
            {tableDistribution.length > 0 && (
              <table style={{ marginTop: '0.5rem', width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.68rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' as const, padding: '2px 6px', fontWeight: 700, color: '#1a6b3c', borderBottom: '1px solid #bbf7d0' }}>Table</th>
                    <th style={{ textAlign: 'left' as const, padding: '2px 6px', fontWeight: 700, color: '#1a6b3c', borderBottom: '1px solid #bbf7d0' }}>Zone</th>
                    <th style={{ textAlign: 'center' as const, padding: '2px 6px', fontWeight: 700, color: '#1a6b3c', borderBottom: '1px solid #bbf7d0' }}>Guests</th>
                  </tr>
                </thead>
                <tbody>
                  {tableDistribution.map((t) => (
                    <tr key={t.name}>
                      <td style={{ padding: '2px 6px', fontWeight: 600, color: t.name === 'Unseated' ? '#dc2626' : '#0f2d1f' }}>{t.name}</td>
                      <td style={{ padding: '2px 6px', fontSize: '0.64rem', color: t.name === 'Unseated' ? '#dc2626' : '#6b7280' }}>{t.zone}</td>
                      <td style={{ textAlign: 'center' as const, padding: '2px 6px', fontWeight: 700, color: t.name === 'Unseated' ? '#dc2626' : '#c9a84c' }}>{t.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {/* Reunion Host Years — only if non-empty */}
          {selectedChapter.reunionHostYears && (
            <div style={{ ...styles.detailCard, aspectRatio: 'unset', minHeight: '80px', gridColumn: '1 / -1' }}>
              <div style={styles.detailLabel}>REUNION HOST</div>
              <div style={styles.detailValue}>{selectedChapter.reunionHostYears}</div>
            </div>
          )}
        </div>

        {/* Chapter Fact — only if non-empty */}
        {selectedChapter.chapterFact && (
          <div style={styles.factSection}>
            <div style={styles.detailLabel}>CHAPTER FACT</div>
            <p style={styles.factText}>{selectedChapter.chapterFact}</p>
          </div>
        )}

        {/* Chapter Leadership — all chapters */}
        <div style={{ marginTop: '1.25rem', padding: '1rem', backgroundColor: 'rgba(10,31,20,0.85)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.25)' }}>
          <div style={styles.detailLabel}>CHAPTER LEADERSHIP</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '0.5rem' }}>
            <div style={{ padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.25)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#fff' }}>{selectedChapter.presidentName}</div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.85)', marginTop: '2px', fontWeight: 500 }}>President</div>
            </div>
            <div style={{ padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.25)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#fff' }}>{selectedChapter.secretaryName}</div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.85)', marginTop: '2px', fontWeight: 500 }}>Secretary</div>
            </div>
          </div>
        </div>

        {/* Executives — Georgia chapter only, 2-column card grid */}
        {executives.length > 0 && (
          <div style={{ marginTop: '1.25rem', padding: '1rem', backgroundColor: 'rgba(10,31,20,0.85)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.25)' }}>
            <div style={styles.detailLabel}>EXECUTIVE BOARD</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '0.5rem' }}>
              {executives.map((exec) => (
                <div key={exec.id} style={{ padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.25)' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#fff' }}>{exec.name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.85)', marginTop: '2px', fontWeight: 500 }}>{exec.title}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Heritage Members — Georgia chapter only */}
        {heritageMembers.length > 0 && (
          <div style={{ marginTop: '1.25rem', padding: '1rem', backgroundColor: 'rgba(10,31,20,0.85)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.25)' }}>
            <div style={styles.detailLabel}>HERITAGE MEMBERS</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '0.5rem' }}>
              {heritageMembers.map((member) => (
                <div key={member.id} style={{ padding: '10px 12px', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.25)' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#fff' }}>{member.name}</div>
                  <div style={{ fontSize: '0.72rem', color: '#c9a84c', marginTop: '2px', fontWeight: 500 }}>Heritage Member</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Main chapters grid ──────────────────────────────────────
  return (
    <div style={styles.container}>
      <h1 style={styles.title}>KDU Chapters</h1>
      <p style={styles.subtitle}>{chapters.length} chapters worldwide</p>

      <div style={styles.grid}>
        {chapters.map((ch) => (
          <div
            key={ch.id}
            style={{ ...styles.card, ...(ch.reunionHostYears ? { paddingBottom: '28px' } : {}) }}
            onClick={() => setSelectedChapter(ch)}
            role="button"
            tabIndex={0}
            aria-label={`${ch.name} chapter`}
          >
            <div style={styles.cardName}>{ch.name.toUpperCase()}</div>
            <div style={styles.cardYear}>
              {ch.inceptionYear ? `Est. ${ch.inceptionYear}` : '—'}
            </div>
            {ch.reunionHostYears && (
              <div style={styles.reunionHostBanner}>
                🏆 REUNION HOST {ch.reunionHostYears}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '1rem',
    maxWidth: '100vw',
    overflowX: 'hidden',
    minHeight: '100vh',
  },
  title: {
    fontSize: '2.4rem',
    fontWeight: 900,
    color: '#d32f2f',
    margin: '0 0 0.25rem',
    textAlign: 'center',
    textShadow: '0 2px 4px rgba(0,0,0,0.45)',
    letterSpacing: '0.01em',
  },
  subtitle: {
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.85)',
    textAlign: 'center',
    margin: '0 0 1rem',
    textShadow: '0 1px 2px rgba(0,0,0,0.4)',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#6b7280',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    width: '100%',
    boxSizing: 'border-box',
  },
  card: {
    aspectRatio: '1 / 1',
    minWidth: 0,
    boxSizing: 'border-box',
    backgroundColor: 'rgba(30, 60, 120, 0.75)',
    borderRadius: '14px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.6rem',
    cursor: 'pointer',
    boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
    transition: 'transform 0.15s',
    backdropFilter: 'blur(4px)',
    overflow: 'hidden',
    position: 'relative',
  },
  cardName: {
    fontSize: '1.2rem',
    fontWeight: 800,
    color: '#fff',
    letterSpacing: '0.02em',
    lineHeight: 1.15,
    textAlign: 'center',
    wordBreak: 'break-word',
    maxWidth: '100%',
  },
  cardYear: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#f5c842',
    marginTop: '6px',
    letterSpacing: '0.02em',
    textShadow: '0 1px 2px rgba(0,0,0,0.4)',
  },
  reunionHostBanner: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#c9a84c',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: '10px 8px',
    textAlign: 'center',
    letterSpacing: '0.04em',
    borderRadius: '0 0 14px 14px',
  },
  // ── Detail page styles ──────────────────────────────────────
  backBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1rem',
    color: '#1a6b3c',
    fontWeight: 600,
    cursor: 'pointer',
    padding: '0.5rem 0',
    marginBottom: '0.5rem',
  },
  detailTitle: {
    fontSize: '1.2rem',
    fontWeight: 700,
    color: '#1a6b3c',
    textAlign: 'center',
    margin: '0 0 1rem',
    letterSpacing: '0.05em',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
  },
  detailCard: {
    aspectRatio: '1 / 1',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '14px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.75rem',
  },
  detailLabel: {
    fontSize: '0.75rem',
    fontWeight: 800,
    color: '#1a6b3c',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  detailValue: {
    fontSize: '0.95rem',
    color: '#1f2937',
    fontWeight: 700,
    textAlign: 'center',
    wordBreak: 'break-word',
  },
  factSection: {
    padding: '0.75rem',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '14px',
    marginTop: '10px',
  },
  factText: {
    fontSize: '0.8rem',
    color: '#374151',
    margin: '4px 0 0',
    lineHeight: 1.4,
  },
};
